// MaturaQuadraticAxisShiftClosedTask.js
// Zadanie maturalne zamknięte – przesunięcie funkcji g(x) = f(x - k).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// =====================================================================
// GENERATOR
// =====================================================================

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 14.3:
 *   f ma oś symetrii x = 1, g(x) = f(x − 3)  →  oś symetrii g: x = 4 (D).
 */
function createOriginalTask() {
  const options = [
    { label: "A", value: -2, isCorrect: false },
    { label: "B", value: 1, isCorrect: false },
    { label: "C", value: 3, isCorrect: false },
    { label: "D", value: 4, isCorrect: true },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaQuadraticAxisShiftClosedTask",
    data: { axisF: 1, k: 3, axisG: 4, options, correctIndex },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: "14.3",
    },
  };
}

// dodajemy config z opcjonalnym axisOverride
export function generateTask(config = {}) {
  if (config.variant === "original") return createOriginalTask();

  const axisOverride =
    config && typeof config.axisOverride === "number"
      ? config.axisOverride
      : null;

  // jeśli podano axisOverride, bierzemy go; inaczej losujemy jak dotychczas
  const axisF = axisOverride !== null
    ? axisOverride
    : pick([-2, -1, 0, 1, 2, 3]); // oś symetrii f

  let k;
  do {
    k = pick([-4, -3, -2, -1, 1, 2, 3, 4]);
  } while (axisF + k < -5 || axisF + k > 5); // żeby liczby były przyjemne

  const axisG = axisF + k;

  // kilka typowych zmyłek
  const wrong1 = axisF; // zapomniany przesuw
  const wrong2 = axisF - k; // pomylony znak
  const wrong3 = axisF + 2 * k; // przesuw „podwójny”

  const valuesRaw = [
    { value: axisG, isCorrect: true },
    { value: wrong1, isCorrect: false },
    { value: wrong2, isCorrect: false },
    { value: wrong3, isCorrect: false },
  ];

  // eliminujemy ewentualne duplikaty
  const seen = new Set();
  const unique = [];
  valuesRaw.forEach((opt) => {
    let v = opt.value;
    while (seen.has(v)) v += 1;
    seen.add(v);
    unique.push({ ...opt, value: v });
  });

  const shuffled = shuffle(unique).map((opt, idx) => ({
    label: LETTERS[idx],
    value: opt.value,
    isCorrect: opt.isCorrect,
  }));

  const correctIndex = shuffled.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaQuadraticAxisShiftClosedTask",
    data: {
      axisF,
      k,
      axisG,
      options: shuffled,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_14_3" },
  };
}


// =====================================================================
// RENDER TREŚCI
// =====================================================================

export function renderTask({ data }) {
  const { axisF, k, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Funkcja kwadratowa <InlineMath math={'f'} /> ma oś symetrii{" "}
        <InlineMath math={`x=${axisF}`} />.
      </p>
      <p>
        Funkcja <InlineMath math={'g'} /> jest określona dla każdej liczby
        rzeczywistej <InlineMath math={'x'} /> wzorem{" "}
        <InlineMath math={`g(x) = f(x ${k >= 0 ? "-" : "+"} ${Math.abs(k)})`} />.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Osią symetrii wykresu funkcji <InlineMath math={'g'} /> jest prosta o
        równaniu:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; x = ${opt.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// INPUT
// =====================================================================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// =====================================================================
// SPRAWDZANIE
// =====================================================================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// =====================================================================
// ODPOWIEDŹ I WYJAŚNIENIE
// =====================================================================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return (
    <p style={{ textAlign: "left" }}>
      <InlineMath math={`${correct.label}.\\; x = ${correct.value}`} />
    </p>
  );
}

export function renderExplanation({ data }) {
  const { axisF, k, axisG } = data;

  const sign = k >= 0 ? "+" : "-";
  const absK = Math.abs(k);

  return (
    <div style={{ textAlign: "left" }} className="space-y-3">
      <p>
        <strong>Krok 1.</strong> Z treści zadania wiemy, że oś symetrii wykresu
        funkcji <InlineMath math={'f'} /> ma równanie:
      </p>

      <BlockMath math={`x = ${axisF}`} />

      <p>
        <strong>Krok 2.</strong> Zależność <InlineMath math={'g(x)=f(x-k)'} /> oznacza
        przesunięcie wykresu funkcji <InlineMath math={'f'} /> wzdłuż osi{" "}
        <InlineMath math={'Ox'} /> o <InlineMath math={'k'} /> jednostek (dla{" "}
        <InlineMath math={'k>0'} /> w prawo, dla <InlineMath math={'k<0'} /> w lewo).
        Oś symetrii przesuwa się o tę samą wartość.
      </p>

      <p>
        <strong>Krok 3.</strong> Do współrzędnej osi symetrii dodajemy <InlineMath math={'k'} />:
      </p>

      <BlockMath math={`x = ${axisF} + (${k})`} />
      <BlockMath math={`x = ${axisF} ${sign} ${absK} = ${axisG}`} />

      <p>
        Zatem osią symetrii wykresu funkcji <InlineMath math={'g'} /> jest prosta:
      </p>

      <BlockMath math={`x = ${axisG}`} />
    </div>
  );
}


export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    typeof userAnswer.value === "undefined"
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, value } = userAnswer;

  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; x = ${value}`} />
    </div>
  );
}
